import { useTheme } from "@mui/material/styles";
import { useKeycloak } from "@react-keycloak/web";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { INTERVIEW_NAVIGATION_TABS } from "../../../constants";
import { interviewTableSkeleton } from "../../../containers/Home/Skeleton/InterviewsSegmentCardSkeleton";
import { artifactAtom } from "../../../stores/jotai/artifacts/artifacts";
import {
  baseAtom,
  getViewMode,
  selectEditingInterview,
  selectInterview,
} from "../../../stores/jotai/base";
import {
  clearSpeakerAndDictionaryData,
  interviewAtom,
} from "../../../stores/jotai/interviews/currentInterview";
import { discoverySessionAtom } from "../../../stores/jotai/discovery-session";
import { interviewFindingsAtom } from "../../../stores/jotai/insights/interviewFindings";
import { interviewsAtom } from "../../../stores/jotai/interviews/interviews";
import { useGetAssemblies } from "../../../tanstackQuery/queries/assemblies";
import { InterviewList } from "./material-table-interview-list";
import moment from "moment";

// Removes transcript, audio and insights
const REVERT_DIALOG =
  "Are you sure you want to remove the audio and transcript and all of its insights?";

export default function MaterialInterviewList(props: any) {
  const { interviews } = props;
  const setInterviewState = useSetAtom(interviewAtom);
  const [base, setBase] = useAtom(baseAtom);
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [discoverySession] = useAtom(discoverySessionAtom);
  const [interviewFindings] = useAtom(interviewFindingsAtom);
  const allInterviews = useAtomValue(interviewsAtom);
  const artifacts = useAtomValue(artifactAtom);
  const [assemblies, setAssemblies] = useState(undefined);
  const [selectionSource, setSelectionSource] = useState<"table" | "external">(
    "external"
  );
  const prevSelectedInterviewRef = useRef<string | null>(null);
  const theme = useTheme();

  const { data: assembliesData, isLoading: isAssembliesLoading } =
    useGetAssemblies(
      { accessToken: keycloak?.token },
      { groupID: base?.team?.groupID, sessionID: discoverySession?.sessionID },
      { enabled: !!(base?.team?.groupID && discoverySession?.sessionID) }
    );

  useEffect(() => {
    if (assembliesData) {
      setAssemblies(assembliesData);
    }
  }, [isAssembliesLoading]);

  const rowIndex = useMemo(() => {
    if (!interviews || typeof base.selectedInterview !== "string")
      return undefined;

    if (selectionSource === "table") return base.selectedInterviewIndex;

    const sortedInterviews = [...interviews]?.sort(
      (a, b) => moment(b.created).valueOf() - moment(a.created).valueOf()
    );
    return sortedInterviews?.findIndex(
      ({ interviewID }) => interviewID === base.selectedInterview
    );
  }, [interviews, base.selectedInterview]);
  
  
  useEffect(() => {
    const prevSelectedInterview = prevSelectedInterviewRef.current;

    if (
      base.selectedInterview &&
      base.selectedInterview !== prevSelectedInterview &&
      selectionSource === "external"
    ) {
      setBase((prevBase) => {
        return { ...prevBase, selectedInterviewIndex: rowIndex };
      });
    }
    prevSelectedInterviewRef.current = base.selectedInterview;
  }, [rowIndex, interviews, setBase]);

  return isAssembliesLoading ? (
    <>{interviewTableSkeleton}</>
  ) : (
    <InterviewList
      {...props}
      artifacts={artifacts}
      baseAtomBridge={base}
      user={base.user}
      setOpenInterviewListDrawer={props?.setOpenInterviewListDrawer}
      openInterviewListDrawer={props?.openInterviewListDrawer}
      team={base.team}
      org={base.org}
      interviewFindings={interviewFindings}
      showingDrafts={
        getViewMode(base, interviews) === INTERVIEW_NAVIGATION_TABS.DRAFT
      }
      shouldSeeExport={true}
      scrubDucmentDialog={REVERT_DIALOG}
      templateList={[]}
      switchDraftMode={() => {}}
      readyForDraftMessage={() => {}}
      save={() => {}}
      remove={() => {}}
      removeAudio={() => {}}
      handleRevertDocument={() => {}}
      bridgeSelectInterview={(interviewID: string, staticRowIndex?: number) => {
        selectInterview(setBase, interviewID, allInterviews, staticRowIndex);
        clearSpeakerAndDictionaryData(setInterviewState);
        setSelectionSource("table");
        navigate("/discovery");
        prevSelectedInterviewRef.current = interviewID;
      }}
      bridgeEditInterview={(payload: any) => {
        selectEditingInterview(setBase, payload);
      }}
      theme={theme}
      assemblies={assemblies}
    />
  );
}
